import { formatDate } from '@angular/common';
import { PaTableColumn, PaTableColumnType, PaTableData, PaTableIconText } from '../models/pa-table.model';
import { DataListItem, DataListType } from '../../pa-data-list/models/pa-data-list.model';
import { FileFromServer } from 'src/app/core/models/files';
import {
  PA_TABLE_DATE_FORMAT,
  PA_TABLE_DATE_LOCALE,
  PA_TABLE_DATETIME_FORMAT,
} from '../pa-table-date-formats.constants';

function getRawField(item: PaTableData, field: string): unknown {
  return item[field];
}

function getAsDate(raw: unknown): Date | null {
  if (raw instanceof Date) {
    return raw;
  }
  if (raw && typeof raw === 'object' && 'date' in raw && (typeof raw.date === 'string' || typeof raw.date === 'number')) {
    return new Date(raw.date);
  }
  if (typeof raw === 'string' || typeof raw === 'number') {
    return new Date(raw);
  }
  return null;
}

function getAsNumber(raw: unknown): number {
  if (typeof raw === 'number') {
    return raw;
  }
  if (typeof raw === 'string') {
    const parsed = parseFloat(raw);
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
}

function getAsBoolean(raw: unknown): boolean {
  return Boolean(raw);
}

function getAsTags(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    return raw as string[];
  }
  return [];
}

function getAsArrayLabels(raw: unknown): string[] {
  const list = Array.isArray(raw) ? raw : [raw];

  return list
    .map((item) => {
      if (typeof item === 'string') {
        return item;
      }

      if (item && typeof item === 'object' && 'name' in item && typeof item.name === 'string') {
        return item.name;
      }

      if (item && typeof item === 'object' && 'label' in item && typeof item.label === 'string') {
        return item.label;
      }

      return '';
    })
    .filter((value) => value.length > 0);
}

function isEmptyTextValue(raw: unknown): boolean {
  return raw == null || (typeof raw === 'string' && raw.trim().length === 0);
}

function getAsFilesOrImages(raw: unknown): Array<string | FileFromServer> {
  if (Array.isArray(raw)) {
    return raw as Array<string | FileFromServer>;
  }
  return [];
}

function getAsIconText(raw: unknown): PaTableIconText | null {
  if (raw && typeof raw === 'object') {
    return raw as PaTableIconText;
  }
  return null;
}

function getAsLink(
  raw: unknown,
): { href: string; label: string } | null {
  if (typeof raw === 'string' && raw.trim().length > 0) {
    return {
      href: raw,
      label: raw,
    };
  }

  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const link = raw as { href?: unknown; label?: unknown };
  if (typeof link.href !== 'string' || link.href.trim().length === 0) {
    return null;
  }

  return {
    href: link.href,
    label:
      typeof link.label === 'string' && link.label.trim().length > 0
        ? link.label
        : link.href,
  };
}

/**
 * Преобразование строки в формат pa-data-list.
 * Используется только в шаблоне мобильного вида сгруппированных таблиц.
 */
export function mapRowToDataListItemsUtil(item: PaTableData, columns: PaTableColumn[]): DataListItem[] {
  if (!columns || columns.length === 0) {
    return [];
  }

  return columns
    .map((col, index) => {
      if (!col.visibleOnMobile || index === 0) {
        return null;
      }

      const label = col.header;
      const rawValue = getRawField(item, col.field);

      if (col.type === PaTableColumnType.Tags) {
        const tags = getAsTags(rawValue);
        if (tags.length === 0) {
          return null;
        }
        return {
          label,
          type: DataListType.TAGS,
          value: tags,
        } as DataListItem;
      }

      if (col.type === PaTableColumnType.Array) {
        const labels = getAsArrayLabels(rawValue);
        if (labels.length === 0) {
          return null;
        }
        return {
          label,
          type: DataListType.TEXT_EXPANDABLE_LIST,
          value: labels,
          countShowElements: col.arrayMaxVisible ?? 1,
        } as DataListItem;
      }

      if (col.type === PaTableColumnType.Files || col.type === PaTableColumnType.Images) {
        const files = getAsFilesOrImages(rawValue).filter((f): f is FileFromServer => typeof f !== 'string');
        if (files.length === 0) {
          return null;
        }
        return {
          label,
          type: DataListType.FILES,
          value: files,
        } as DataListItem;
      }

      if (col.type === PaTableColumnType.Telegram) {
        if (isEmptyTextValue(rawValue)) {
          return null;
        }
        return {
          label,
          type: DataListType.TELEGRAM,
          value: (rawValue as string) || '',
        } as DataListItem;
      }

      if (col.type === PaTableColumnType.Email) {
        if (isEmptyTextValue(rawValue)) {
          return null;
        }
        return {
          label,
          type: DataListType.EMAIL,
          value: (rawValue as string) || '',
        } as DataListItem;
      }

      if (col.type === PaTableColumnType.Phone) {
        if (isEmptyTextValue(rawValue)) {
          return null;
        }
        return {
          label,
          type: DataListType.PHONE,
          value: (rawValue as string) || '',
        } as DataListItem;
      }

      if (col.type === PaTableColumnType.Link) {
        const link = getAsLink(rawValue);
        if (!link) {
          return null;
        }
        return {
          label,
          type: DataListType.LINK,
          value: link.label,
          url: link.href,
        } as DataListItem;
      }

      let formatted = '';

      if (col.type === PaTableColumnType.IconText) {
        const iconText = getAsIconText(rawValue);
        return {
          label,
          type: 'iconText',
          value: {
            icon: iconText?.icon || col.icon,
            severity: iconText?.severity,
            text: iconText?.text,
          },
        } as DataListItem;
      } else if (col.type === PaTableColumnType.Date) {
        const date = getAsDate(rawValue);
        if (!date) {
          return null;
        }
        const hasRecurrentPeriod =
          rawValue &&
          typeof rawValue === 'object' &&
          'recurrent_period' in rawValue &&
          typeof rawValue.recurrent_period === 'string' &&
          rawValue.recurrent_period.length > 0;

        formatted = formatDate(date, PA_TABLE_DATE_FORMAT, PA_TABLE_DATE_LOCALE);

        return {
          label,
          type: DataListType.TEXT,
          value: formatted,
          ...(hasRecurrentPeriod ? { icon: 'repeat' } : {}),
        } as DataListItem;
      } else if (col.type === PaTableColumnType.DateTime) {
        const date = getAsDate(rawValue);
        if (!date) {
          return null;
        }
        formatted = formatDate(date, PA_TABLE_DATETIME_FORMAT, PA_TABLE_DATE_LOCALE);
      } else if (col.type === PaTableColumnType.Number) {
        const num = getAsNumber(rawValue);
        formatted = num.toLocaleString('ru-RU', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        });
      } else if (col.type === PaTableColumnType.Currency) {
        const num = getAsNumber(rawValue);
        formatted = num.toLocaleString('ru-RU', {
          style: 'currency',
          currency: 'RUB',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      } else if (col.type === PaTableColumnType.Boolean) {
        formatted = getAsBoolean(rawValue) ? 'Да' : 'Нет';
      } else if (col.type === PaTableColumnType.Select) {
        const name = (rawValue as { name?: string } | null)?.name ?? '';
        if (name.trim().length === 0) {
          return null;
        }
        formatted = name;
      } else {
        if (isEmptyTextValue(rawValue)) {
          return null;
        }
        formatted = String(rawValue);
      }

      return {
        label,
        type: DataListType.TEXT,
        value: formatted,
      } as DataListItem;
    })
    .filter((v): v is DataListItem => v !== null);
}
